<template>
  <div class="page-wrapper">
    <section class="background">
      <div class="content banner d-flex">
        <div class="d-flex flex-column align-center justify-center pr-4 pr-sm-0">
          <h1 class="visually-hidden"> Telemonial </h1>
          <logo-text-svg class="text-header-logo" color="var(--v-text-base)" />
          <p class="pb-2">the future of media recommendations</p>
          <v-btn :to="{ name: 'login' }" elevation="0" color="primaryText" outlined rounded class="call-to-action">
            login
          </v-btn>
          <v-btn :to="{ name: 'create-account' }" elevation="0" color="primary" rounded class="call-to-action"
            >create account</v-btn
          >
        </div>
        <div class="waterfall-wrapper">
          <img :src="posterWaterfallImg" class="poster-waterfall" />
        </div>
      </div>
    </section>

    <section class="card">
      <div class="content">
        <!-- TODO: Change to "Trending" and base off of popularity only over past 2 weeks -->
        <h2>Popular</h2>
        <div class="chip-radio-wrapper">
          <chip-radio
            v-model="activeTrendingMedium"
            :options="
              Object.values($mediums).map((medium) => ({ label: userFriendlyMediumWord(medium), value: medium }))
            "
            class="chip-radio"
          ></chip-radio>
        </div>
        <div ref="trending-entries" class="trending-entries">
          <div
            class="trending-entry"
            v-for="work in trendingWorks[activeTrendingMedium].slice(0, trendingEntriesMaxItems)"
            :key="work.id + work.medium"
          >
            <poster-blurred-margins
              :src="work.posterSource"
              :to="{ name: 'work', params: { medium: work.medium, workId: work.id } }"
              vertical
              rounded
            >
            </poster-blurred-margins>
          </div>
        </div>
        <div class="see-more-section">
          <router-link :to="{ name: 'explore', params: { medium: activeTrendingMedium } }">see more</router-link>
        </div>
      </div>
    </section>

    <section class="background">
      <div class="content">
        <h2>What is Telemonial?</h2>
        <div class="info-section-content">
          <div class="informational">
            <v-icon class="info-section-icon" size="48">mdi-circle-edit-outline</v-icon>
            <div>
              <h3>Write reviews</h3>
              <p>Post reviews for your books, movies, TV shows, podcasts and albums</p>
            </div>
          </div>
          <div class="informational">
            <v-icon class="info-section-icon" size="48">mdi-account-multiple</v-icon>
            <div>
              <h3>Connect with friends</h3>
              <p>Add your friends to keep up with their activity</p>
            </div>
          </div>
          <div class="informational">
            <v-icon class="info-section-icon" size="48">mdi-magnify</v-icon>
            <div>
              <h3>Discover new works</h3>
              <p>Search our database to find new recommendations</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="card" v-if="!isMobileBuild()">
      <div class="content">
        <h2>Download the app</h2>
        <div class="d-flex justify-center">
          <a href="https://apps.apple.com/us/app/telemonial/id1611051356" class="mr-6">
            <img :src="appStoreBadgeSvg" class="app-link" />
          </a>
          <a href="https://play.google.com/store/apps/details?id=com.telemonial.app&hl=en">
            <img :src="googlePlayBadge" class="app-link" />
          </a>
        </div>
        <img :src="MobileScreenshot" class="mobile-screenshot" />
      </div>
    </section>
  </div>
</template>

<script>
import { computeWidthFromRef } from "../../util/domHelpers";
import { maxElementsInWidth } from "../../util/maxElementsInWidth";
import { mediums } from "../../util/enums";
import { userFriendlyMediumWord } from "../../util/mediumHelpers";
import appStoreBadgeSvg from "./../../../assets/attributions/Download_on_the_App_Store_Badge_US-UK_RGB_blk_092917.svg";
import googlePlayBadge from "./../../../assets/attributions/GetItOnGooglePlay_Badge_Web_color_English.png";
import PosterWaterfall5ColCropped from "./../../../assets/PosterWaterfall5ColCropped.png";
import PosterWaterfall4ColCropped from "./../../../assets/PosterWaterfall4ColCropped.png";
import PosterWaterfall3ColCropped from "./../../../assets/PosterWaterfall3ColCropped.png";
import MobileScreenshot from "./../../../assets/MobileScreenshot-2024-08-13.png";
import getTopWorks from "../../mixins/getTopWorks";
import ChipRadio from "../../components/chip-radio.vue";
import PosterBlurredMargins from "../../components/poster-blurred-margins.vue";
import LogoTextSvg from "../../components/logo-text-svg.vue";
import isMobileBuild from "../../util/isMobileBuild";

export default {
  name: "home-no-login",

  mixins: [getTopWorks],

  components: { ChipRadio, PosterBlurredMargins, LogoTextSvg },

  data: () => {
    return {
      isMounted: false,

      placeholderPoster: "https://image.tmdb.org/t/p/original/qJ2tW6WMUDux911r6m7haRef0WH.jpg",
      appStoreBadgeSvg,
      googlePlayBadge,
      MobileScreenshot,

      trendingEntryWidthPixels: 100,
      trendingEntriesGapPixels: undefined,
      trendingEntryAvailableWidthPixels: undefined,

      activeTrendingMedium: mediums.FILM,

      trendingWorks: {
        [mediums.FILM]: [],
        [mediums.BOOK]: [],
        [mediums.TV_SHOW]: [],
        [mediums.ALBUM]: [],
        [mediums.PODCAST]: [],
      },
    };
  },

  computed: {
    trendingEntriesMaxItems() {
      if (!this.isMounted) return 0;
      return maxElementsInWidth(
        this.trendingEntryWidthPixels,
        this.trendingEntriesGapPixels,
        this.trendingEntryAvailableWidthPixels,
      );
    },

    trendingEntriesResizeObserver() {
      return new ResizeObserver(() => {
        this.trendingEntryAvailableWidthPixels = computeWidthFromRef(this, "trending-entries");
      });
    },

    posterWaterfallImg() {
      if (this.$vuetify.breakpoint.mdAndUp) {
        return PosterWaterfall5ColCropped;
      } else if (this.$vuetify.breakpoint.sm) {
        return PosterWaterfall4ColCropped;
      } else {
        return PosterWaterfall3ColCropped;
      }
    },
  },

  async mounted() {
    await this.loadTrendingWorks();

    this.$nextTick(() => {
      this.trendingEntryAvailableWidthPixels = computeWidthFromRef(this, "trending-entries");
      this.trendingEntriesGapPixels = this.computeTrendingEntriesGapPixels();
      this.trendingEntriesResizeObserver.observe(this.$refs["trending-entries"]);
      this.isMounted = true;
    });
  },

  async destroyed() {
    this.trendingEntriesResizeObserver.disconnect();
  },

  methods: {
    isMobileBuild,
    userFriendlyMediumWord,

    computeTrendingEntriesGapPixels() {
      const element = this.$refs["trending-entries"];
      const style = window.getComputedStyle(element);
      if (!style.gap.endsWith("px")) {
        throw Error("Expected gap to be defined in pixels");
      }
      const gapPixels = parseInt(style.gap);
      if (isNaN(gapPixels)) {
        throw Error("Unable to parse the gap", style.gap);
      }
      return gapPixels;
    },

    async loadTrendingWorks() {
      for (const medium of Object.values(mediums)) {
        const items = await this.getTopWorksV1(
          medium,
          20,
          "reviewCount", // TODO: Use an enum
          0,
        );
        this.trendingWorks[medium] = items;
      }
    },
  },
};
</script>

<style>
:root {
  --trending-entries-height: 150px;
}
</style>

<style scoped>
.page-wrapper {
  margin: -28px;
}

@media (min-width: 600px) {
  .page-wrapper {
    margin: -36px;
  }
}

.page-wrapper div {
  padding: auto;
}

section {
  padding: 16px;
}

.content {
  max-width: 1200px;
  margin: auto;
}

.text-header-logo {
  max-height: 42px;
  margin-bottom: 12px;
}

@media (min-width: 600px) {
  .text-header-logo {
    margin-bottom: 20px;
  }
}

h1 {
  line-height: 1;
  font-size: 36px;
}

h2 {
  text-align: center;
  color: var(--v-text-lighten1);
  font-size: 26px;
  margin-bottom: 8px;
}

h3 {
  font-size: 16px;
}

p {
  margin-bottom: 0;
}

.waterfall-wrapper {
  display: flex;
  justify-content: center;
}

.poster-waterfall {
  mask-image: linear-gradient(transparent, black 25% 75%, transparent 100%);
  margin: auto;
  width: 100%;
  max-height: 300px;
  object-fit: cover;
}

.banner {
  gap: 16px;
}

.banner div {
  width: 50%;
  text-align: center;
}

@media (max-width: 599px) {
  .banner div {
    width: 60%;
  }

  .banner div:last-of-type {
    width: 40%;
    text-align: center;
  }
}

.call-to-action {
  width: 200px;
  margin-bottom: 16px;
}
.call-to-action:last-of-type {
  margin-bottom: 0;
}

.chip-radio-wrapper {
  margin: 0 -16px;
  padding: 4px 0;
  overflow-x: scroll;
  overflow-y: visible;
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
}

/* Hide scrollbar for Chrome, Safari and Opera */
.chip-radio-wrapper::-webkit-scrollbar {
  display: none;
}

.chip-radio {
  padding: 0 16px;
  min-width: 500px;
}

.trending-entries {
  margin-top: 12px;
  margin-bottom: 12px;
  height: var(--trending-entries-height);

  display: flex;
  justify-content: space-around;
  gap: 16px;
}

.trending-entry {
  width: calc(var(--trending-entries-height) * 2 / 3);
  height: var(--trending-entries-height);
}

.see-more-section {
  text-align: end;

  a {
    color: var(--v-text-primary);
    text-decoration: underline;
  }
}

.info-section-content {
  display: flex;
  flex-direction: column;
  gap: 26px;
}
.info-section-content div {
  /* Equally space elements */
  flex-basis: 0;
  flex-grow: 1;
}

.informational {
  display: flex;
  align-items: center;
}

.informational p {
  font-size: 16px;
  margin-bottom: 0;
}

.info-section-content .info-section-icon {
  font-size: 48px;
  display: block;
  margin-right: 16px;
}
@media (min-width: 660px) {
  .info-section-content {
    flex-direction: row;
    gap: 24px;
  }

  .informational {
    flex-direction: column;
    text-align: center;
  }

  .informational .info-section-icon {
    margin-right: 0;
  }

  .informational {
    h3 {
      font-size: 22px;
    }
    p {
      font-size: 16px;
    }
  }
}

.app-link {
  height: 56px;
}
.app-link:hover {
  transform: scale(1.1);
}

.mobile-screenshot {
  display: block;
  margin: auto;
  width: 100%;
  max-width: 300px;
}
</style>
